import React from "react";
import { DateTime } from "luxon";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { X, User, Calendar, Clock, Trash2, LogOut } from "lucide-react";
import { useClientAuth } from "../ClientAuthContext";
import { useClientAppointments, useCancelAppointment } from "../hooks/useClientAppointments";
import type { DashboardTab, AppointmentItem } from "../types";
import { resolveMediaUrl } from "@/lib/resolveMediaUrl";
import { salonNow } from "@/lib/time";
import { useTranslation } from "react-i18next";
import { getLuxonLocale } from "@/lib/i18n/dateLocales";

interface Props {
    onClose: () => void;
}

function MyWaitlist() {
    const { t } = useTranslation();
    const { state } = useClientAuth();
    const accessToken = state.auth?.accessToken ?? null;
    const [rows, setRows] = React.useState<any[]>([]);
    const load = React.useCallback(async () => {
        if (!accessToken) return; // require_client endpoint — needs the client token
        try {
            const { fetchMyWaitlist } = await import("@/lib/services/waitlistPublicService");
            const data = await fetchMyWaitlist(accessToken);
            setRows((data?.entries ?? []).filter((e: any) => e.status === "active"));
        } catch { /* portal without waitlist — hide silently */ }
    }, [accessToken]);
    React.useEffect(() => { load(); }, [load]);

    const cancel = async (token: string) => {
        if (!confirm(t("widget.waitlist.cancel_confirm", "Leave the waiting list?"))) return;
        try {
            const { cancelWaitlistEntry } = await import("@/lib/services/waitlistPublicService");
            await cancelWaitlistEntry(token);
            load();
        } catch { /* ignore */ }
    };

    if (!rows.length) return null;
    return (
        <div className="px-4 pb-2">
            <div className="mb-1 text-xs font-semibold text-muted-foreground">
                {t("widget.waitlist.mine_title", "My waiting list")}
            </div>
            {rows.map((e) => (
                <div key={e.id} className="mb-2 flex items-center justify-between rounded-lg border border-dashed p-2 text-sm">
                    <div>
                        <div>{e.service_title}{e.staff_name ? ` · ${e.staff_name}` : ""}</div>
                        <div className="text-xs text-muted-foreground">{e.date_from} — {e.date_to}</div>
                    </div>
                    {e.claim_token && (
                        <button className="text-xs text-destructive underline"
                                onClick={() => cancel(e.claim_token)}>
                            {t("widget.waitlist.cancel_mine", "Cancel")}
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}

export default function ClientAppointmentsView({ onClose }: Props) {
    const { t, i18n } = useTranslation();
    const { state, actions } = useClientAuth();
    const [tab, setTab] = React.useState<DashboardTab>("upcoming");
    const [cancelingId, setCancelingId] = React.useState<number | null>(null);

    const { data, isLoading, error } = useClientAppointments(state.auth?.accessToken ?? null);
    const cancelMutation = useCancelAppointment(state.auth?.accessToken ?? null);
    const luxonLocale = getLuxonLocale(i18n.resolvedLanguage || i18n.language || "en");

    const handleCancel = async (recordId: number) => {
        if (!confirm(t("widget.client.appointments.cancel_confirm", "Are you sure you want to cancel this appointment?"))) return;
        setCancelingId(recordId);
        try {
            await cancelMutation.mutateAsync(recordId);
        } finally {
            setCancelingId(null);
        }
    };

    const handleLogout = () => {
        actions.logout();
        onClose();
    };

    // Use salonNow() instead of new Date().
    // We memoize it to avoid re-calculating lists on every strict-mode render, 
    // though for this view it's not critical. Refreshes on mount.
    const now = React.useMemo(() => salonNow(), []);
    const salonTz = now.zoneName ?? "UTC";

    // Split appointments into upcoming and past
    const { upcoming, past } = React.useMemo(() => {
        const items = data?.items ?? [];
        const up: AppointmentItem[] = [];
        const pa: AppointmentItem[] = [];

        for (const item of items) {
            // Backend sends date="YYYY-MM-DD", time="HH:mm" in UTC.
            // Construct UTC instant.
            const startUtc = DateTime.fromISO(`${item.date}T${item.time}`, { zone: "utc" });

            if (startUtc >= now) {
                up.push(item);
            } else {
                pa.push(item);
            }
        }

        // Sort upcoming by date ascending
        up.sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
        // Sort past by date descending
        pa.sort((a, b) => b.date.localeCompare(a.date) || b.time.localeCompare(a.time));

        return { upcoming: up, past: pa };
    }, [data?.items, now]);

    const displayed = tab === "upcoming" ? upcoming : past;

    return (
        <div className="bg-background h-[100dvh] min-h-[100dvh]">
            <div className="w-full h-full mx-auto sm:max-w-[420px] md:max-w-[520px]">
                <Card className="bg-card ios-shadow border-0 h-full flex flex-col sm:rounded-2xl sm:overflow-hidden">
                    {/* Header */}
                    <div className="p-4 border-b border-border/10 flex items-center justify-between gap-3">
                        <h2 className="text-lg font-semibold flex-1 truncate">{t("widget.client.appointments.title", "My appointments")}</h2>
                        <Button variant="ghost" size="icon" onClick={handleLogout} title={t("widget.client.logout", "Log out")}>
                            <LogOut className="w-5 h-5" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={onClose}>
                            <X className="w-5 h-5" />
                        </Button>
                    </div>

                    {/* Tabs */}
                    <div className="px-4 pt-4 flex gap-2">
                        <Button
                            variant={tab === "upcoming" ? "default" : "outline"}
                            className="flex-1 rounded-full text-sm"
                            onClick={() => setTab("upcoming")}
                        >
                            {t("widget.client.appointments.upcoming", "Upcoming")}
                        </Button>
                        <Button
                            variant={tab === "past" ? "default" : "outline"}
                            className="flex-1 rounded-full text-sm"
                            onClick={() => setTab("past")}
                        >
                            {t("widget.client.appointments.past", "Past")}
                        </Button>
                    </div>

                    <MyWaitlist />

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {isLoading && (
                            <div className="text-center text-muted-foreground py-8">
                                {t("widget.client.appointments.loading", "Loading appointments...")}
                            </div>
                        )}

                        {error && (
                            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                                {t("widget.client.appointments.error", "Could not load appointments")}
                            </div>
                        )}

                        {!isLoading && !error && displayed.length === 0 && (
                            <div className="text-center text-muted-foreground py-8">
                                {tab === "upcoming"
                                    ? t("widget.client.appointments.none_upcoming", "No upcoming appointments")
                                    : t("widget.client.appointments.none_past", "No past appointments")}
                            </div>
                        )}

                        {displayed.map((item) => (
                            <AppointmentCard
                                key={item.id}
                                item={item}
                                salonTz={salonTz}
                                showCancel={tab === "upcoming" && item.can_cancel && !item.deleted}
                                onCancel={() => handleCancel(item.id)}
                                isCanceling={cancelingId === item.id}
                                luxonLocale={luxonLocale}
                            />
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
}

function AppointmentCard({
    item,
    salonTz,
    showCancel,
    onCancel,
    isCanceling,
    luxonLocale,
}: {
    item: AppointmentItem;
    salonTz: string;
    showCancel: boolean;
    onCancel: () => void;
    isCanceling: boolean;
    luxonLocale: string;
}) {
    const { t } = useTranslation();
    const avatarUrl = item.master.avatar_url
        ? resolveMediaUrl(`/media/staff/${item.master.avatar_url}`)
        : undefined;

    // Convert UTC -> Salon
    const startSalon = DateTime.fromISO(`${item.date}T${item.time}`, { zone: "utc" }).setZone(salonTz);

    return (
        <div
            className={`rounded-xl border bg-muted/30 p-4 space-y-3 ${item.deleted ? "opacity-50" : ""
                }`}
        >
            {/* Date & Time */}
            <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(startSalon, luxonLocale)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>{startSalon.toFormat("HH:mm")}</span>
                </div>
                {item.deleted && (
                    <span className="ml-auto text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                        {t("widget.client.appointments.cancelled", "Cancelled")}
                    </span>
                )}
            </div>

            {/* Service */}
            <div className="font-medium">{item.service.title}</div>

            {/* Master */}
            <div className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                    <AvatarImage src={avatarUrl} />
                    <AvatarFallback><User className="w-4 h-4" /></AvatarFallback>
                </Avatar>
                <span className="text-sm text-muted-foreground">{item.master.name}</span>
            </div>

            {/* Cancel Button */}
            {showCancel && (
                <Button
                    variant="outline"
                    size="sm"
                    className="w-full rounded-lg text-destructive border-destructive/30 hover:bg-destructive/10"
                    onClick={onCancel}
                    disabled={isCanceling}
                >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {isCanceling
                        ? t("widget.client.appointments.canceling", "Canceling...")
                        : t("widget.client.appointments.cancel", "Cancel appointment")}
                </Button>
            )}
        </div>
    );
}

function formatDate(date: DateTime, locale: string): string {
    return date.setLocale(locale).toFormat("d LLLL yyyy");
}
